import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, CardActionArea, Divider, Stack, Typography} from 'views/components';
import {MailOutline} from 'views/icons';
import {TaskEditor, Translate, translate} from 'views/module';

import CardBody from './CardBody';

import backend from 'utils/backend';
import moment from 'moment';

const Card = (props) => {
  const {
    item,
    onItemUpdate,
    hasUnreadMessages,
    statusProgressWrap: progressWrap,
  } = props;

  const [cardItem, setCardItem] = React.useState(item);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = React.useState(false);
  const [isCallStatusDropdownOpen, setIsCallStatusDropdownOpen] = React.useState(false);
  const [isTaskEditorOpen, setIsTaskEditorOpen] = React.useState(false);

  React.useEffect(() => {
    setCardItem(item);
  }, [item]);

  const clientCareplan = cardItem.clientCarePlan;
  const clientStatusLog = cardItem.clientLastLogStatusUpdate;
  const clientLastMessage = cardItem.clientLastMessage;
  const clientRn = cardItem.clientRn;
  const clientName = cardItem.clientName;
  const clientCreatedAt = cardItem.createdAt;
  const status = cardItem.status;
  const hasUnreadMessage = hasUnreadMessages(clientRn, clientLastMessage);
  const callStatus = cardItem.clientCallStatus?.introCallStatus;
  const leadQuality = clientCareplan?.leadQuality;
  const isAnyOverlayOpen = isStatusDropdownOpen || isCallStatusDropdownOpen;
  const [clientFirstName = '', ...clientLastNameParts] = (clientName || '').trim().split(/\s+/).filter(Boolean);
  const clientLastName = clientLastNameParts.join(' ');

  const clientRelationships = React.useMemo(() => {
    const relationships = Object.values(cardItem.relationships ?? {});
    if (!relationships.length) return [];

    return relationships.slice().sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      return a.name.localeCompare(b.name);
    });
  }, [cardItem.relationships]);

  const primaryRelationship = clientRelationships[0];
  const notSetLabel = translate('clientLead.ClientLeadList.Card2.SubtitleValue.NotSet');
  const clientLocation = clientCareplan?.generalInformation?.address || notSetLabel;
  const clientCreatedAtLabel = clientCreatedAt ? moment(clientCreatedAt).format('MMM D, YYYY') : '-';

  const clientDaysInStage = React.useMemo(() => {
    if (!clientStatusLog) {
      return Math.floor(moment().diff(moment(clientCreatedAt), 'days', true));
    } else {
      return Math.floor(moment().diff(moment(clientStatusLog.createdAt), 'days', true));
    }
  }, [clientStatusLog, clientCreatedAt]);

  const clientDaysInStageText = React.useMemo(() => {
    return translate(
        `clientLead.ClientLeadList.Card.SubtitleValue.${clientDaysInStage === 1 ? 'Day' : 'Days'}`,
        {number: clientDaysInStage},
    );
  }, [clientDaysInStage]);

  const clientDaysSinceLastContact = React.useMemo(() => {
    if (!clientLastMessage) return null;
    return Math.floor(moment().diff(moment(clientLastMessage.createdAt), 'days', true));
  }, [clientLastMessage]);

  const clientDaysSinceLastContactText = React.useMemo(() => {
    if (clientDaysSinceLastContact === null) return null;
    return translate(
        `clientLead.ClientLeadList.Card.SubtitleValue.${clientDaysSinceLastContact === 1 ? 'Day' : 'Days'}`,
        {number: clientDaysSinceLastContact},
    );
  }, [clientDaysSinceLastContact]);

  const handleSetStatus = async (nextStatus, message = null) => {
    if (!nextStatus || nextStatus === status) {
      return;
    }

    const previousItem = cardItem;
    const optimisticItem = {
      ...cardItem,
      status: nextStatus,
      clientLastLogStatusUpdate: {
        ...cardItem.clientLastLogStatusUpdate,
        createdAt: new Date().toISOString(),
      },
    };

    setCardItem(optimisticItem);
    onItemUpdate?.(clientRn, optimisticItem);

    try {
      await progressWrap(async () => {
        const body = {
          status: nextStatus,
        };

        if (message) {
          body.message = message;
        }

        const {clientDetail, updateLog} = await backend.client.updateStatus(clientRn, body);
        const nextItem = {
          ...optimisticItem,
          ...clientDetail,
          clientLastLogStatusUpdate: updateLog ?? clientDetail.clientLastLogStatusUpdate,
        };

        setCardItem(nextItem);
        onItemUpdate?.(clientRn, nextItem);
      });
    } catch (error) {
      setCardItem(previousItem);
      onItemUpdate?.(clientRn, previousItem);
      console.error(error);
    }
  };

  const handleSetCallStatus = async (nextCallStatus) => {
    if (!nextCallStatus || nextCallStatus === callStatus) {
      return;
    }

    const previousItem = cardItem;
    const optimisticItem = {
      ...cardItem,
      clientCallStatus: {
        ...(cardItem.clientCallStatus ?? {}),
        introCallStatus: nextCallStatus,
      },
    };

    setCardItem(optimisticItem);
    onItemUpdate?.(clientRn, optimisticItem);

    try {
      await progressWrap(async () => {
        const {callStatus: nextCallStatusData} = await backend.client.editCallStatus(clientRn, {
          introCallStatus: nextCallStatus,
        });
        const nextItem = {
          ...optimisticItem,
          clientCallStatus: nextCallStatusData,
        };

        setCardItem(nextItem);
        onItemUpdate?.(clientRn, nextItem);
      });
    } catch (error) {
      setCardItem(previousItem);
      onItemUpdate?.(clientRn, previousItem);
      console.error(error);
    }
  };

  const closeTaskEditor = () => {
    setIsTaskEditorOpen(false);
  };

  const handleOpenTaskEditor = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsTaskEditorOpen(true);
  };

  const handleSubmitTask = (taskData) => {
    progressWrap(async () => {
      try {
        await backend.task.addTask(taskData);
      } finally {
        closeTaskEditor();
      }
    });
  };

  return (
    <React.Fragment>
      {
        isTaskEditorOpen &&
          <Box
            sx={{
              height: '100%',
              textAlign: 'left',
              width: '100%',
              minWidth: 0,
            }}
          >
            <CardBody
              callStatus={callStatus}
              clientCreatedAtLabel={clientCreatedAtLabel}
              clientDaysInStageText={clientDaysInStageText}
              clientDaysSinceLastContactText={clientDaysSinceLastContactText}
              clientLastMessage={clientLastMessage}
              clientLocation={clientLocation}
              clientName={clientName}
              hasUnreadMessage={hasUnreadMessage}
              leadQuality={leadQuality}
              notSetLabel={notSetLabel}
              onCallStatusOverlayChange={setIsCallStatusDropdownOpen}
              onStatusOverlayChange={setIsStatusDropdownOpen}
              primaryRelationship={primaryRelationship}
              selectedCallStatus={callStatus}
              selectedStage={status}
              setSelectedCallStatus={handleSetCallStatus}
              setSelectedStage={handleSetStatus}
              footer={
                <Box
                  sx={{
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  <TaskEditor
                    autoFocusTitle
                    controlRowSx={{
                      flex: {
                        xs: '1 1 280px',
                        lg: '1 1 100%',
                      },
                    }}
                    onCancel={closeTaskEditor}
                    onSubmit={handleSubmitTask}
                    task={{
                      connectionInfo: [{
                        connectionRn: clientRn,
                        firstName: clientFirstName,
                        lastName: clientLastName,
                      }],
                    }}
                  />
                </Box>
              }
            />
          </Box>
      }
      {
        !isTaskEditorOpen &&
          <CardActionArea
            onClick={(event) => {
              if (!isAnyOverlayOpen) {
                return;
              }

              event.preventDefault();
              event.stopPropagation();
            }}
            sx={{
              height: '100%',
              textAlign: 'left',
              width: '100%',
              minWidth: 0,
            }}
            to={`/client/${clientRn}`}
          >
            <CardBody
              callStatus={callStatus}
              clientCreatedAtLabel={clientCreatedAtLabel}
              clientDaysInStageText={clientDaysInStageText}
              clientDaysSinceLastContactText={clientDaysSinceLastContactText}
              clientLastMessage={clientLastMessage}
              clientLocation={clientLocation}
              clientName={clientName}
              hasUnreadMessage={hasUnreadMessage}
              leadQuality={leadQuality}
              notSetLabel={notSetLabel}
              onCallStatusOverlayChange={setIsCallStatusDropdownOpen}
              onStatusOverlayChange={setIsStatusDropdownOpen}
              primaryRelationship={primaryRelationship}
              selectedCallStatus={callStatus}
              selectedStage={status}
              setSelectedCallStatus={handleSetCallStatus}
              setSelectedStage={handleSetStatus}
              footer={
                <Stack
                  direction={{
                    xs: 'column',
                    sm: 'row',
                  }}
                  alignItems={{
                    xs: 'flex-start',
                    sm: 'center',
                  }}
                  spacing={1}
                  sx={{
                    minHeight: '16px',
                    minWidth: 0,
                  }}
                >
                  <Box
                    component='button'
                    onClick={handleOpenTaskEditor}
                    sx={{
                      'background': 'none',
                      'border': 'none',
                      'color': '#A8A49E',
                      'cursor': 'pointer',
                      'display': 'inline-flex',
                      'alignItems': 'center',
                      'gap': '4px',
                      'fontSize': '14px',
                      'lineHeight': '16px',
                      'p': 0,
                      'px': 1,
                      'minHeight': '16px',
                      'borderRadius': '8px',
                      'transition': 'background-color 120ms ease, color 120ms ease',
                      '&:hover': {
                        backgroundColor: '#F7F4EF',
                        color: 'primary.main',
                      },
                      '&:focus-visible': {
                        outline: '2px solid #EBBDAD',
                        outlineOffset: 2,
                        backgroundColor: '#FFF7F4',
                      },
                    }}
                  >
                    <Box component='span'>+</Box>
                    <Translate expression='tasks.Common.NewTask' />
                  </Box>
                  {
                    hasUnreadMessage &&
                    <Divider
                      orientation='vertical'
                      flexItem
                      sx={{
                        display: {
                          xs: 'none',
                          sm: 'block',
                        },
                        borderColor: '#EBEBEB',
                        my: '1.25px',
                      }}
                    />
                  }
                  {
                    hasUnreadMessage &&
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <MailOutline
                        sx={{
                          fontSize: '16px',
                          color: '#DA7955',
                        }}
                      />
                      <Typography
                        component='div'
                        sx={{
                          color: '#DA7955',
                          fontSize: '12px',
                          fontWeight: 400,
                        }}
                      >
                        <Translate expression='clientLead.ClientLeadList.Card.Subtitles.NewMessage' />
                      </Typography>
                    </Box>
                  }
                </Stack>
              }
            />
          </CardActionArea>
      }
    </React.Fragment>
  );
};

Card.propTypes = {
  item: PropTypes.object.isRequired,
  onItemUpdate: PropTypes.func,
  hasUnreadMessages: PropTypes.func.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(Card));
